import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder} from '@angular/forms';
import {AssetId, DeviceSlotId, ProjectId, UserId} from '@webmate-web-core/shared/types/ids';
import {mergeMap, take} from 'rxjs/operators';
import {DeviceSlot} from '@webmate-web-core/shared/types/devices/device-slot';
import {DeviceSlotService} from '@webmate-web-core/shared/services/device/device-slot.service';
import {StartupScript} from '@webmate-web-core/shared/types/startupscript';
import {AssetAndBlobService} from '@webmate-web-core/shared/services/assetAndBlob.service';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {WebmateToolConfig} from '../modaltoolcontainer/modaltoolcontainer.service';
import {TOOL_CONTAINER_DATA} from '../modaltoolcontainer/modaltoolcontainer.component';
import {CheckboxChangeEvent} from 'primeng/checkbox/checkbox.interface';


export class ScriptManagerToolConfig implements WebmateToolConfig {
    toolName = 'Startup Script Manager';

    constructor(public slotId: DeviceSlotId, public projectId: ProjectId) {
    }
}

@Component({
    templateUrl: './script-manager.component.html',
    styleUrls: ['./script-manager.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScriptManagerComponent implements OnInit {

    userId: UserId;

    message: string = '';
    deviceSlot?: DeviceSlot = undefined;
    scripts: StartupScript[];
    busySendingReport = false;

    @ViewChild('fileInput')
    uploader: ElementRef;

    files: any[] = [];

    uploading = false;
    validTypes = ['.sh', '.ps1', '.scpt'];


    constructor(private fb: UntypedFormBuilder,
                @Inject(TOOL_CONTAINER_DATA) public data: ScriptManagerToolConfig,
                private deviceSlotService: DeviceSlotService,
                private assetService: AssetAndBlobService,
                private userService: UserService,
                private cdRef: ChangeDetectorRef) {
    }

    ngOnInit() {
        this.deviceSlotService.getSlotInProject$(this.data.slotId, this.data.projectId).subscribe(next => {
            this.deviceSlot = next;
            this.cdRef.markForCheck();
        });
        this.deviceSlotService.getScriptsInProject(this.data.projectId, false).pipe(take(1)).subscribe(scripts => {
            this.scripts = scripts;
            this.cdRef.markForCheck();
        });
        this.userService.getCurrentUserData().pipe(take(1)).subscribe(u => {
            this.userId = u.id;
            this.cdRef.markForCheck();
        });
    }

    getTypeString() {
        return this.validTypes.map(x => `*${x}`).join(', ');
    }

    selectFiles(event: Event) {
        const target = event.target as HTMLInputElement;
        const files = target.files as FileList;
        Array.from(files).forEach(f => this.uploadFile(f));
    }

    dropFiles(event: File[]) {
        event.forEach(f => {
            if (!!this.validTypes.find(t => f.name.endsWith(t))) {
                this.uploadFile(f);
            }
        });
    }

    openFileChooser() {
        this.uploader.nativeElement.click();
    }

    uploadFile(f: File) {
        this.uploading = true;
        this.cdRef.markForCheck();
        this.assetService.putBlob(f, this.data.projectId).pipe(
            mergeMap(blobId => {
                return this.assetService.createAsset(blobId, this.userId, 'script', this.data.projectId, f.name, 'startupscript');
            }),
            mergeMap(assetId => this.deviceSlotService.addScriptToSlot(assetId, this.data.slotId)),
            mergeMap(() => this.deviceSlotService.getScriptsInProject(this.data.projectId, true)),
            take(1))
        .subscribe(scripts => {
            this.uploading = false;
            this.scripts = scripts;
            this.cdRef.markForCheck();
        });
    }

    deleteScript(assetId: AssetId) {
        if (assetId) {
            this.assetService.deleteAsset(assetId)
            .pipe(
                mergeMap(() => this.deviceSlotService.getScriptsInProject(this.data.projectId, true)),
                take(1))
            .subscribe(scripts => {
                this.scripts = scripts;
                this.cdRef.markForCheck();
            });
        }
    }

    scriptEnabled(s: StartupScript) {
        return !!this.deviceSlot!.propertyBag?.properties_by_project && !!this.deviceSlot!.propertyBag?.properties_by_project[this.data.projectId]?.startupScripts?.find((ss: any) => ss.asset === s.asset);
    }

    toggleScript(event: CheckboxChangeEvent, assetId: AssetId) {
        if (!!assetId) {
            (event.checked ? this.deviceSlotService.addScriptToSlot(assetId, this.data.slotId) : this.deviceSlotService.removeScriptFromSlot(assetId, this.data.slotId)).pipe(take(1)).subscribe();
        }
    }

    download(s: StartupScript) {
        this.assetService.downloadAsset(s.asset, s.name);
    }
}
